<template>
  <div class="notification-panel" :class="{ 'show': show }">
    <div class="notification-header">
      <h3>{{ title }}</h3>
      <button class="close-btn" @click="close">
        <i class="fa-solid fa-xmark"></i>
      </button>
    </div>
    
    <div class="notification-body">
      <div 
        v-for="(item, index) in items" 
        :key="index" 
        class="notification-item"
        :class="{ 'unread': item.isNew }"
      >
        <!-- Avatar or icon display -->
        <div class="notification-icon" v-if="item.icon">
          <i :class="item.icon"></i>
        </div>
        <div class="notification-avatar" v-else-if="item.avatar">
          <img :src="item.avatar" :alt="item.title">
        </div>
        <div class="notification-doc-icon" v-else-if="item.type === 'document'">
          <i class="fa-regular fa-file-lines"></i>
        </div>
        <div class="notification-case-icon" v-else-if="item.type === 'case'">
          <i class="fa-solid fa-briefcase"></i>
        </div>
        <div class="notification-icon-default" v-else>
          <i class="fa-solid fa-bell"></i>
        </div>
        
        <!-- Content section -->
        <div class="notification-content">
          <div class="notification-header-row">
            <span class="notification-title">{{ item.title }}</span>
            <span class="notification-label" v-if="item.label">— {{ item.label }}</span>
          </div>
          
          <div class="notification-time-row">
            <span class="notification-badge" v-if="item.isNew">new</span>
            <span class="notification-time">{{ item.time }}</span>
          </div>
        </div>
        
        <!-- Action button for item -->
        <div class="notification-action" v-if="item.action">
          <button @click="item.action.handler" class="action-btn">
            <i :class="item.action.icon"></i>
          </button>
        </div>
      </div>
      
      <!-- Empty state -->
      <div v-if="items.length === 0" class="empty-state">
        <i class="fa-regular fa-bell-slash"></i>
        <p>No {{ type === 'message' ? 'messages' : 'notifications' }} at the moment</p>
      </div>
    </div>
    
    <div class="notification-footer">
      <button class="mark-read-btn" @click="markAllAsRead">
        <i class="fa-solid fa-check-double"></i>
        Mark all as read
      </button>
      <button class="clear-btn" @click="clearAll">
        <i class="fa-solid fa-broom"></i>
        Clear {{ type === 'message' ? 'messages' : 'notifications' }}
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'NotificationPanel',
  props: {
    title: {
      type: String,
      default: 'Notifications'
    },
    items: {
      type: Array,
      default: () => []
    },
    show: {
      type: Boolean,
      default: false
    },
    type: {
      type: String,
      default: 'notification', // 'notification' or 'message'
      validator: (value) => ['notification', 'message'].includes(value)
    }
  },
  methods: {
    close() {
      this.$emit('close');
    },
    markAllAsRead() {
      this.$emit('mark-all-read');
    },
    clearAll() {
      this.$emit('clear-all');
    }
  }
}
</script>

<style scoped>
.notification-panel {
  position: absolute;
  top: 60px; /* Position it below the topbar */
  right: 20px;
  width: 400px;
  max-width: 90vw;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
  z-index: 1000;
  overflow: hidden;
  display: none;
  flex-direction: column;
  max-height: 90vh;
}

/* Add a triangle pointer at the top */
.notification-panel::before {
  content: '';
  position: absolute;
  top: -10px;
  right: 25px; /* Position triangle near the notification button */
  width: 0;
  height: 0;
  border-left: 10px solid transparent;
  border-right: 10px solid transparent;
  border-bottom: 10px solid #fff;
}

.notification-panel.show {
  display: flex;
}

.notification-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
  background-color: #f9fafb;
}

.notification-header h3 {
  font-size: 18px;
  margin: 0;
  color: #111827;
  font-weight: 600;
}

.close-btn {
  background: transparent;
  border: none;
  color: #6b7280;
  cursor: pointer;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
}

.close-btn:hover {
  background: #f3f4f6;
  color: #374151;
}

.notification-body {
  overflow-y: auto;
  max-height: 60vh;
  flex: 1;
}

.notification-item {
  display: flex;
  padding: 16px 20px;
  border-bottom: 1px solid #f3f4f6;
  align-items: flex-start;
  position: relative;
}

.notification-item:hover {
  background-color: #f9fafb;
}

.notification-item.unread {
  background-color: rgba(59, 130, 246, 0.05);
  border-left: 3px solid #3b82f6;
}

.notification-icon,
.notification-avatar,
.notification-doc-icon,
.notification-case-icon,
.notification-icon-default {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  background-color: #f0f4ff;
  color: #4682ff;
  flex-shrink: 0;
}

.notification-avatar {
  border-radius: 50%;
  overflow: hidden;
}

.notification-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.notification-doc-icon {
  background-color: #f0f4ff;
  color: #4682ff;
}

.notification-case-icon {
  background-color: #fff5f0;
  color: #ff8a47;
}

.notification-content {
  flex: 1;
  min-width: 0;
}

.notification-header-row {
  display: flex;
  align-items: center;
  margin-bottom: 4px;
  flex-wrap: wrap;
}

.notification-title {
  font-weight: 600;
  color: #333;
  margin-right: 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.notification-label {
  color: #666;
  font-weight: 400;
  white-space: nowrap;
}

.notification-time-row {
  display: flex;
  align-items: center;
}

.notification-badge {
  background-color: #fef3c7;
  color: #92400e;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  margin-right: 8px;
  text-transform: lowercase;
}

.notification-time {
  color: #888;
  font-size: 13px;
}

.notification-action {
  margin-left: 8px;
}

.action-btn {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background-color: #f5f5f5;
  color: #555;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.action-btn:hover {
  background-color: #e5e5e5;
}

.empty-state {
  padding: 40px 20px;
  text-align: center;
  color: #888;
}

.empty-state i {
  font-size: 32px;
  margin-bottom: 12px;
  color: #ccc;
}

.notification-footer {
  display: flex;
  padding: 16px 20px;
  border-top: 1px solid #f3f4f6;
  gap: 12px;
  background-color: #f9fafb;
}

.mark-read-btn {
  flex: 1;
  padding: 10px;
  border-radius: 8px;
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background-color: #f3f4f6;
  color: #374151;
  border: 1px solid #e5e7eb;
}

.mark-read-btn:hover {
  background-color: #e5e7eb;
}

.clear-btn {
  flex: 1;
  padding: 10px;
  border-radius: 8px;
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background-color: #3b82f6;
  color: white;
  border: none;
}

.clear-btn:hover {
  background-color: #2563eb;
}

@media screen and (max-width: 768px) {
  .notification-panel {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    max-width: 100%;
    max-height: 100%;
    border-radius: 0;
  }
  
  .notification-body {
    max-height: none;
  }
}
</style> 